import React, { useState } from 'react';
import axios from 'axios';
import PortfolioLightbox from '../components/PortfolioLightbox';
import WhatsAppButton from '../components/WhatsAppButton';
import { 
  DoorClosed, 
  Frame, 
  Wrench, 
  Lock, 
  Hammer, 
  Sparkles,
  Phone,
  Mail,
  MapPin,
  Star,
  ChevronRight,
  CheckCircle2,
  Check,
  Home as HomeIcon,
  Flame,
  Maximize2,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useToast } from '../hooks/use-toast';
import { companyInfo, services, portfolio, testimonials, faqs, pricingPackages } from '../mock';

const iconMap = {
  DoorClosed,
  Frame,
  Wrench,
  Lock,
  Hammer,
  Sparkles,
  Home: HomeIcon,
  Flame,
  Maximize2,
  Trash2
};

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const categories = ['All', ...new Set(portfolio.map(item => item.category))];
  const filteredPortfolio = activeFilter === 'All' 
    ? portfolio 
    : portfolio.filter(item => item.category === activeFilter);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/quote-requests`, formData);
      if (response.data.success) {
        toast({
          title: "Quote Request Sent!",
          description: response.data.message || "We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send quote request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-amber-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-900 to-amber-800 text-amber-50 sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_build-showcase-148/artifacts/4oqualfy_temp_image_B69CD62C-A65E-43CF-A04E-C52C20BE3294.WEBP" 
                alt="AM Carpentry Logo" 
                className="h-12 w-12 object-contain"
              />
              <h1 className="text-2xl font-bold">{companyInfo.name}</h1>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="#services" className="hover:text-amber-200 transition-colors">Services</a>
              <a href="#pricing" className="hover:text-amber-200 transition-colors">Pricing</a>
              <a href="#portfolio" className="hover:text-amber-200 transition-colors">Portfolio</a>
              <a href="#about" className="hover:text-amber-200 transition-colors">About</a>
              <a href="#testimonials" className="hover:text-amber-200 transition-colors">Testimonials</a>
              <Button 
                onClick={scrollToContact}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Get Quote
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100 via-orange-50 to-amber-50 opacity-60"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h2 className="text-5xl md:text-6xl font-bold text-amber-950 mb-6 leading-tight">
              {companyInfo.tagline}
            </h2>
            <p className="text-xl text-amber-900 mb-8 leading-relaxed">
              {companyInfo.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={scrollToContact}
                size="lg" 
                className="bg-amber-800 hover:bg-amber-900 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
              >
                Request Free Quote
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-amber-800 text-amber-800 hover:bg-amber-100 px-8 py-6 text-lg"
              >
                View Our Work
              </Button>
            </div>
          </div>
        </div>
        {/* Decorative Element */}
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-300 rounded-full blur-3xl opacity-20"></div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-amber-950 mb-4">Our Services</h2>
            <p className="text-lg text-amber-800 max-w-2xl mx-auto">
              Specialized door fitting and carpentry services delivered with precision and care
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="border-2 border-amber-200 hover:border-amber-400 hover:shadow-xl transition-all duration-300 group"
                >
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-600 to-amber-700 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-amber-950 mb-3">{service.title}</h3>
                    <p className="text-amber-800 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-amber-950 mb-4">Door Fitting Prices</h2>
            <p className="text-lg text-amber-800 max-w-2xl mx-auto">
              Transparent pricing for quality carpentry work. All prices are for installation labour
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {pricingPackages.map((pkg) => {
              const Icon = iconMap[pkg.icon];
              return (
                <Card 
                  key={pkg.id}
                  className={`border-2 ${
                    pkg.popular 
                      ? 'border-amber-600 shadow-xl ring-2 ring-amber-200' 
                      : 'border-amber-200'
                  } hover:shadow-lg hover:border-amber-400 transition-all duration-300`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                      <span className="bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        Popular
                      </span>
                    </div>
                  )}
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-amber-950 mb-2">{pkg.name}</h3>
                    <div className="text-3xl font-bold text-amber-800 mb-3">{pkg.price}</div>
                    <p className="text-amber-700 text-sm mb-4">{pkg.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12 max-w-3xl mx-auto">
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-amber-950 mb-3">Important Notes</h3>
              <ul className="text-left space-y-2 text-amber-800">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Prices shown are for labour only - door and materials not included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Prices may vary based on door size, complexity, and site conditions</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Multiple services can be combined - contact us for package pricing</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>All work comes with a 2-year warranty</span>
                </li>
              </ul>
            </div>
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="bg-amber-800 hover:bg-amber-900 text-white px-8"
            >
              Request Free Quote
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-amber-950 mb-4">Our Work</h2>
            <p className="text-lg text-amber-800 max-w-2xl mx-auto mb-8">
              Explore our portfolio of quality craftsmanship and precision door fitting
            </p>
            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <Button
                  key={category}
                  onClick={() => setActiveFilter(category)}
                  variant={activeFilter === category ? "default" : "outline"}
                  className={activeFilter === category 
                    ? "bg-amber-800 hover:bg-amber-900 text-white" 
                    : "border-amber-800 text-amber-800 hover:bg-amber-100"
                  }
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPortfolio.map((project, idx) => (
              <Card
                key={project.id}
                onClick={() => setLightboxIndex(idx)}
                className="overflow-hidden group hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
                data-testid={`portfolio-card-${project.id}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-amber-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                    <span className="text-white text-sm font-medium bg-amber-800/80 px-3 py-1 rounded-full">
                      Click to view
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="text-sm text-amber-700 font-medium mb-2">{project.category}</div>
                  <h3 className="text-xl font-bold text-amber-950 mb-2">{project.title}</h3>
                  <p className="text-amber-800">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Lightbox */}
      <PortfolioLightbox
        images={filteredPortfolio}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNext={() => setLightboxIndex((prev) => (prev + 1) % filteredPortfolio.length)}
        onPrev={() => setLightboxIndex((prev) => (prev - 1 + filteredPortfolio.length) % filteredPortfolio.length)}
      />

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-amber-950 mb-6">About AM Carpentry</h2>
              <p className="text-lg text-amber-800 mb-6 leading-relaxed">
                At AM Carpentry, we believe that every door tells a story. With years of experience in precision door fitting and custom carpentry, we've built our reputation on quality craftsmanship, attention to detail, and exceptional customer service.
              </p>
              <p className="text-lg text-amber-800 mb-6 leading-relaxed">
                Our specialty in door fittings means we understand the intricacies of proper installation, from ensuring perfect alignment to selecting the right hardware. Whether it's a modern interior door or a vintage restoration project, we approach each job with the same dedication to excellence.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="text-center p-6 bg-amber-100 rounded-lg">
                  <div className="text-4xl font-bold text-amber-900 mb-2">500+</div>
                  <div className="text-amber-800">Projects Completed</div>
                </div>
                <div className="text-center p-6 bg-amber-100 rounded-lg">
                  <div className="text-4xl font-bold text-amber-900 mb-2">10+</div>
                  <div className="text-amber-800">Years Experience</div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://images.unsplash.com/photo-1589939705384-5185137a7f0f" 
                alt="Carpentry work" 
                className="rounded-lg shadow-lg h-64 w-full object-cover"
              />
              <img 
                src="https://images.pexels.com/photos/5974285/pexels-photo-5974285.jpeg" 
                alt="Detail work" 
                className="rounded-lg shadow-lg h-64 w-full object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-amber-950 mb-4">What Our Clients Say</h2>
            <p className="text-lg text-amber-800 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-amber-200 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <p className="text-amber-900 mb-6 leading-relaxed italic">"{testimonial.content}"</p>
                  <div>
                    <div className="font-bold text-amber-950">{testimonial.name}</div>
                    <div className="text-sm text-amber-700">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-amber-950 mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-amber-800">
                Find answers to common questions about our services
              </p>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border-amber-200">
                  <AccordionTrigger className="text-left text-amber-950 hover:text-amber-800">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-amber-800">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-amber-900 to-amber-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Get Your Free Quote</h2>
              <p className="text-xl text-amber-100">
                Ready to start your project? Contact us today for a free, no-obligation quote
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <Card className="bg-white/10 backdrop-blur-sm border-amber-700">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Name *</label>
                      <Input 
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-white/90 border-amber-600 text-amber-950"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <Input 
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-white/90 border-amber-600 text-amber-950"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone</label>
                      <Input 
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="bg-white/90 border-amber-600 text-amber-950"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Project Details *</label>
                      <Textarea 
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        className="bg-white/90 border-amber-600 text-amber-950"
                        placeholder="Tell us about your door fitting or carpentry project..."
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg disabled:opacity-50"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Quote Request'}
                      <ChevronRight className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <Phone className="w-6 h-6 text-amber-300 mt-1" />
                      <div>
                        <div className="font-medium text-amber-100">Phone</div>
                        <a href={`tel:${companyInfo.phone}`} className="text-lg hover:text-amber-200 transition-colors">
                          {companyInfo.phone}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Mail className="w-6 h-6 text-amber-300 mt-1" />
                      <div>
                        <div className="font-medium text-amber-100">Email</div>
                        <a href={`mailto:${companyInfo.email}`} className="text-lg hover:text-amber-200 transition-colors">
                          {companyInfo.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <MapPin className="w-6 h-6 text-amber-300 mt-1" />
                      <div>
                        <div className="font-medium text-amber-100">Address</div>
                        <p className="text-lg">{companyInfo.address}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-amber-700">
                  <h4 className="text-xl font-bold mb-4">Why Choose Us?</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-amber-300 mt-0.5" />
                      <span>2-Year Warranty on All Work</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-amber-300 mt-0.5" />
                      <span>Free Quotes & Consultations</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-amber-300 mt-0.5" />
                      <span>Professional & Reliable Service</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-amber-300 mt-0.5" />
                      <span>Fully Insured & Licensed</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-950 text-amber-100 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_build-showcase-148/artifacts/4oqualfy_temp_image_B69CD62C-A65E-43CF-A04E-C52C20BE3294.WEBP" 
                  alt="AM Carpentry Logo" 
                  className="h-10 w-10 object-contain bg-white rounded-full p-1"
                />
                <h3 className="text-xl font-bold text-white">{companyInfo.name}</h3>
              </div>
              <p className="text-amber-200">{companyInfo.tagline}</p>
            </div>
            <div>
              <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="hover:text-amber-300 transition-colors">Services</a></li>
                <li><a href="#pricing" className="hover:text-amber-300 transition-colors">Pricing</a></li>
                <li><a href="#portfolio" className="hover:text-amber-300 transition-colors">Portfolio</a></li>
                <li><a href="#about" className="hover:text-amber-300 transition-colors">About</a></li>
                <li><a href="#testimonials" className="hover:text-amber-300 transition-colors">Testimonials</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold text-white mb-4">Business Hours</h4>
              <ul className="space-y-2 text-amber-200">
                <li>Monday - Friday: 5:00 PM - 10:00 PM</li>
                <li>Saturday - Sunday: 10:00 AM - 10:00 PM</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-amber-800 pt-8 text-center text-amber-300">
            <p>&copy; 2025 {companyInfo.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Chat Button */}
      <WhatsAppButton />
    </div>
  );
};

export default Home;